import React from "react";
import { Alert } from "antd";
import useLang from "../../../hooks/useLang";
import {
	BulkOrderOperationError,
	BulkOrderOperationResponse,
} from "../../../api/bulk-order-operations";
import IdListSection from "./IdListSection";

interface ResultPanelsProps {
	result: BulkOrderOperationResponse | null;
	errorResult: BulkOrderOperationError | null;
}

const ResultPanels = ({ result, errorResult }: ResultPanelsProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;

	const copyLabel = common?.copy ?? "Copy";
	const copiedMessage = common?.copied ?? "Copied to clipboard";

	return (
		<>
			{result && (
				<div className="flex flex-col gap-3">
					<h2 className="text-lg font-semibold">
						{common?.resultTitle ?? "Result"}
					</h2>
					<Alert type="success" showIcon message={result.message} />
					<div className="text-sm text-gray-600">
						{common?.totalRequested ?? "Requested"}:{" "}
						{result.totalRequested} •{" "}
						{common?.updatedCount ?? "Updated"}:{" "}
						{result.updatedCount}
					</div>
					{/*
						Every list defaults to [] — the API always sends all of them, but
						IdListSection reads .length immediately, so one missing array
						would blank the whole result panel.
					*/}
					<IdListSection
						title={common?.updatedIds ?? "Updated orders"}
						ids={result.updatedOrderIds ?? []}
						tone="success"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.skippedAtStatus ??
							"Skipped (already at target status)"
						}
						ids={result.skippedAlreadyAtStatusOrderIds ?? []}
						tone="info"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={common?.notFound ?? "Not found"}
						ids={result.notFoundOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.alreadyPaid ??
							"Already paid — skipped (no double credit)"
						}
						ids={result.alreadyPaidOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.skippedIneligible ??
							"Skipped (protected local status)"
						}
						ids={result.skippedIneligibleOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.destinationUpdated ??
							"Updated in the destination country"
						}
						ids={result.destinationUpdatedOrderIds ?? []}
						tone="success"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.skippedAtSection ??
							"Skipped (already on the target zone)"
						}
						ids={result.skippedAlreadyAtSectionOrderIds ?? []}
						tone="info"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					{/*
						Deliver only. These are REAL NEW ORDERS the warehouse is now waiting
						for — the item the buyer handed back. Shown as two lists because the
						operator needs both: the delivered ids to reconcile against AMK's
						sheet, and the new ids to print labels from.
					*/}
					<IdListSection
						title={
							common?.childOrderCreatedFor ??
							"Exchange/return order created for"
						}
						ids={result.childOrderCreatedForOrderIds ?? []}
						tone="success"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.createdChildOrders ??
							"New exchange/return orders — expected back at the warehouse"
						}
						ids={result.createdChildOrderIds ?? []}
						tone="success"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.childOrderAlreadyExisted ??
							"Already had an exchange/return order — none created"
						}
						ids={result.childOrderAlreadyExistedOrderIds ?? []}
						tone="info"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					{/*
						SectionId is overwritten in place and there is no audit table, so this
						breakdown is the only record of the previous assignment. Shown expanded
						rather than in a Collapse for exactly that reason.
					*/}
					{!!result.previousSections?.length && (
						<div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-1">
							<span className="font-medium text-sm">
								{common?.previousSections ?? "Moved off"}
							</span>
							{result.previousSections.map((section) => (
								<span
									key={section.sectionId}
									className="text-sm text-gray-600"
								>
									{section.sectionName ?? "—"} (
									{section.sectionId}) — {section.orderCount}
								</span>
							))}
						</div>
					)}
				</div>
			)}

			{errorResult && (
				<div className="flex flex-col gap-3">
					<Alert
						type="error"
						showIcon
						message={
							common?.errorTitle ??
							"Operation blocked — nothing was written"
						}
						description={errorResult.message}
					/>
					<div className="text-sm text-gray-600">
						{common?.errorRetryNote ??
							"The transaction was rolled back completely. Fix the list and retry."}
					</div>
					<IdListSection
						title={common?.notFound ?? "Not found"}
						ids={errorResult.notFoundOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={common?.notDelivered ?? "Not delivered"}
						ids={errorResult.notDeliveredOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.missingUuid ?? "No UUID — cannot be matched"
						}
						ids={errorResult.missingUuidOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.notFoundInDestination ??
							"Not found in the destination country"
						}
						ids={errorResult.notFoundInDestinationOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
					<IdListSection
						title={
							common?.deliveredWithBalance ??
							"Delivered with balance imported — remove from the list"
						}
						ids={errorResult.deliveredWithBalanceOrderIds ?? []}
						tone="warning"
						copyLabel={copyLabel}
						copiedMessage={copiedMessage}
					/>
				</div>
			)}
		</>
	);
};

export default ResultPanels;
